import { useEffect, useState } from 'react';
import PageWrapper from '../../../../components/Display/PageWrapper';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { useLanguageContext } from '../../../../contexts/LanguageContext';
import { useNavigationContext } from '../../../../contexts/NavigationContext';
import { privateFetch } from '../../../../utils/apiHelper';
import MultipleImagePanel from '../../common/image/MultipleImagePanel';
import ProjectDetail from './ProjectDetail';
import ProjectItem from './ProjectItem';
import ProjectPanel from './ProjectPanel';
import styles from '../../../../components/Display/Wrapper.module.scss';

function ProjectPage({ isActive }) {
  const { setActiveView, navigationContext } = useNavigationContext();
  const [projectList, setProjectList] = useState([]);
  const { accountContext } = useAccountContext();
  const [projectDetail, setProjectDetail] = useState(undefined);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [headerImagesManagerIsOpen, setHeaderImagesManagerIsOpen] = useState(false);
  const [otherImagesManagerIsOpen, setOtherImagesManagerIsOpen] = useState(false);
  const { languageContext } = useLanguageContext();
  const [layout, setLayout] = useState('grid');

  useEffect(() => {
    if (navigationContext.activeProjectId) {
      fetchProjectDetail();
    } else setProjectDetail(undefined);
  }, [navigationContext, languageContext]);

  useEffect(() => {
    fetchProjectList();
  }, [languageContext]);

  const fetchProjectList = async () => {
    await privateFetch(
      'GET',
      'chiennoir',
      `/cu/${accountContext.id}/project/?lang=${languageContext}`
    ).then((res) => {
      setProjectList(res);
    });
  };

  const fetchProjectDetail = async () => {
    await privateFetch(
      'GET',
      'chiennoir',
      `/cu/${accountContext.id}/project/${navigationContext.activeProjectId}/?lang=${languageContext}`
    ).then((res) => {
      if (res) setProjectDetail(res);
    });
  };

  const createProject = async (projectForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      'chiennoir',
      `/cu/${accountContext.id}/project/`,
      'Le projet a bien été ajouté',
      JSON.stringify(projectForm)
    ).then((res) => {
      fetchProjectList();
      setPanelIsOpen(false);
      setLoading(false);
      setActiveView('cngproject', res, 'activeProjectId');
    });
  };

  const updateProject = async (projectForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      'chiennoir',
      `/cu/${accountContext.id}/project/${projectDetail.id}/?lang=${languageContext}`,
      'Le projet a bien été modifié',
      JSON.stringify(projectForm)
    ).then(() => {
      fetchProjectList();
      setPanelIsOpen(false);
      fetchProjectDetail(projectDetail.id);
      setLoading(false);
    });
  };

  const handleDeleteProject = async (project) => {
    await privateFetch(
      'DELETE',
      'chiennoir',
      `/cu/${accountContext.id}/project/${project.id}/`,
      'Le projet a bien été supprimé'
    ).then(() => {
      fetchProjectList();
      setProjectDetail(undefined);
    });
  };

  const updateHeaderProjectImages = async (images) => {
    await privateFetch(
      'PUT',
      'chiennoir',
      `/cu/${accountContext.id}/project/${projectDetail.id}/images/header/`,
      'Les images du projet ont bien été modifiées',
      JSON.stringify({
        header_images: images.map((image) => image.id)
      })
    ).then(() => {
      fetchProjectDetail();
      setLoading(false);
    });
  };

  const updateOtherProjectImages = async (images) => {
    await privateFetch(
      'PUT',
      'chiennoir',
      `/cu/${accountContext.id}/project/${projectDetail.id}/images/other/`,
      'Les images du projet ont bien été modifiées',
      JSON.stringify({
        other_images: images.map((image) => image.id)
      })
    ).then(() => {
      fetchProjectDetail();
      setLoading(false);
    });
  };

  const handleClickProject = (project) => {
    setActiveView('cngproject', project, 'activeProjectId');
  };

  const handleOpenManager = () => {
    setPanelIsOpen(true);
  };
  const toggleLayout = () => {
    setLayout(layout === 'grid' ? 'list' : 'grid');
  };

  const changeLayoutAction = {
    onClick: toggleLayout,
    ariaLabel: "changer le mode d'affichage",
    icon: layout == 'grid' ? 'pi-list' : 'pi-th-large'
  };

  const newProjectAction = {
    onClick: handleOpenManager,
    label: 'Ajouter un projet',
    icon: 'pi-plus'
  };

  const goBackProjectAction = {
    onClick: handleClickProject,
    icon: 'pi-chevron-left',
    label: 'Retour à la liste'
  };

  return (
    <div className={`h-full w-full flex-col ${isActive ? 'flex' : 'hidden'}`}>
      <PageWrapper
        label="Projets"
        labelAction={handleClickProject}
        actions={projectDetail ? [goBackProjectAction] : [changeLayoutAction, newProjectAction]}
        detail={projectDetail}>
        {!projectDetail ? (
          <div className={styles.pageContentContainer}>
            <div className={` ${layout === 'grid' ? styles.gridLayout : styles.listLayout}`}>
              {projectList.map((project, index) => (
                <ProjectItem
                  key={index}
                  project={project}
                  handleClickProject={() => handleClickProject(project)}
                  layout={layout}
                />
              ))}
            </div>
          </div>
        ) : (
          <ProjectDetail
            project={projectDetail}
            handleUpdateProject={handleOpenManager}
            handleUpdateHeaderImages={() => setHeaderImagesManagerIsOpen(true)}
            handleUpdateOtherImages={() => setOtherImagesManagerIsOpen(true)}
            handleDelete={handleDeleteProject}
          />
        )}
        <ProjectPanel
          isOpen={panelIsOpen}
          onClose={setPanelIsOpen}
          project={projectDetail}
          onCreate={createProject}
          onUpdate={updateProject}
          loading={loading}
        />
      </PageWrapper>

      <MultipleImagePanel
        key="header-images"
        loading={loading}
        isOpen={headerImagesManagerIsOpen}
        onClose={() => {
          setHeaderImagesManagerIsOpen(false);
        }}
        images={projectDetail?.header_images}
        onValidate={updateHeaderProjectImages}
      />

      {/* Only for highlight projects */}
      <MultipleImagePanel
        key="other-images"
        loading={loading}
        isOpen={otherImagesManagerIsOpen}
        onClose={() => {
          setOtherImagesManagerIsOpen(false);
        }}
        images={projectDetail?.other_images}
        onValidate={updateOtherProjectImages}
      />
    </div>
  );
}

export default ProjectPage;
